import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo, forwardRef } from 'react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { textStyles, CaptionText, HeadingText } from '@paypalcorp/pp-react-text';
import { devWarning, uniqueId, utilStyles } from '@paypalcorp/pp-react-utils';
var Color;
/** @deprecated */

(function (Color) {
  Color["Accent1"] = "sysColorAccent1Main";
  Color["Accent2"] = "sysColorAccent2Main";
  Color["Accent3"] = "sysColorAccent3Main";
  Color["Accent4"] = "sysColorAccent4Main";
})(Color || (Color = {}));
var colorMap = {
  blue: Color.Accent1,
  magenta: Color.Accent2,
  green: Color.Accent3,
  purple: Color.Accent4
};
var Size;
(function (Size) {
  Size["SMALL"] = "sm";
  Size["LARGE"] = "lg";
})(Size || (Size = {}));
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
    sysBorderRadiusSm = tokens.sysBorderRadiusSm,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
    sysColorAccent1Contrast = tokens.sysColorAccent1Contrast,
    sysColorAccent1Main = tokens.sysColorAccent1Main,
    sysColorAccent2Contrast = tokens.sysColorAccent2Contrast,
    sysColorAccent2Main = tokens.sysColorAccent2Main,
    sysColorAccent3Contrast = tokens.sysColorAccent3Contrast,
    sysColorAccent3Main = tokens.sysColorAccent3Main,
    sysColorAccent4Contrast = tokens.sysColorAccent4Contrast,
    sysColorAccent4Main = tokens.sysColorAccent4Main,
    sysColorBackgroundAlt = tokens.sysColorBackgroundAlt,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
    sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
    sysColorSystemContrast = tokens.sysColorSystemContrast,
    sysSizeCircleMd = tokens.sysSizeCircleMd,
    sysSizeCircleXl = tokens.sysSizeCircleXl,
    sysSizeIndicatorLg = tokens.sysSizeIndicatorLg,
    sysSizeIndicatorMd = tokens.sysSizeIndicatorMd,
    sysSizeIndicatorSm = tokens.sysSizeIndicatorSm,
    sysSizeIndicatorXs = tokens.sysSizeIndicatorXs,
    sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceSm = tokens.sysSpaceSm,
    sysSizeIconSm = tokens.sysSizeIconSm,
    sysSizeIconLg = tokens.sysSizeIconLg,
    sysSpaceXl = tokens.sysSpaceXl,
    sysSpaceXs = tokens.sysSpaceXs,
    sysMotionDurationArrive = tokens.sysMotionDurationArrive;
  var _textStyles = textStyles(tokens),
    textBody = _textStyles.body,
    textCaption = _textStyles.caption,
    textCaptionStrong = _textStyles.captionStrong; // Circle progress meter variables

  var circleRadiusSvg = 50;
  var circleProgressMeterSize = parseInt(sysSizeCircleXl, 10);
  var circleProgressMeterBorderSize = parseFloat(sysSizeIndicatorSm);
  var circleProgressMeterSmSize = parseInt(sysSizeCircleMd, 10);
  var circleProgressMeterSmBorderSize = sysSizeIndicatorXs;
  var progressIndicatorCircleStrokeWidth = circleProgressMeterBorderSize / circleProgressMeterSize * 100;
  var progressIndicatorCircleSmStrokeWidth = parseFloat(circleProgressMeterSmBorderSize) / circleProgressMeterSmSize * 100;
  var colorMapContrast = new Map();
  colorMapContrast.set(Color.Accent1, sysColorAccent1Contrast);
  colorMapContrast.set(Color.Accent2, sysColorAccent2Contrast);
  colorMapContrast.set(Color.Accent3, sysColorAccent3Contrast);
  colorMapContrast.set(Color.Accent4, sysColorAccent4Contrast);
  var colorMapAccent = new Map();
  colorMapAccent.set(Color.Accent1, sysColorAccent1Main);
  colorMapAccent.set(Color.Accent2, sysColorAccent2Main);
  colorMapAccent.set(Color.Accent3, sysColorAccent3Main);
  colorMapAccent.set(Color.Accent4, sysColorAccent4Main);
  /**
   * Get the styles for the height of the linear progress meter
   * @param height string [This should include value with units, example: `10px`, `2rem`]
   * @returns SerializedStyles
   */

  var getProgressMeterHeightStyles = function (height) {
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      height: ", ";\n\n      &::-moz-progress-bar {\n        height: ", ";\n      }\n\n      &::-webkit-progress-value {\n        height: ", ";\n      }\n\n      &::-webkit-progress-bar {\n        height: ", ";\n      }\n    "])), height, height, height, height);
  };
  /**
   * Get the styles for color and background
   * @param color Color
   * @returns SerializedStyles
   */

  var getColorStyles = function (color) {
    var label = "color_".concat(color);
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      label: ", ";\n      color: ", ";\n      background-color: ", ";\n    "])), label, colorMapContrast.get(color), colorMapAccent.get(color));
  };
  /**
   * Check whether the size is small or not
   * @param size Size
   * @returns boolean
   */

  var isSizeSmall = function () {
    var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    return size && size === Size.SMALL;
  };
  /**
   * Get the styles for the color of the linear progress meter
   * @param color Color
   * @param backgroundColor string
   * @returns SerializedStyles
   */

  var getProgressMeterColorStyles = function (color) {
    var backgroundColor = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : sysColorBackgroundAlt;
    var meterColor = colorMapAccent.get(color);
    var label = "container_color_".concat(color);
    return css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      label: ", ";\n      progress {\n        color: ", ";\n\n        &::-moz-progress-bar {\n          background-color: ", ";\n        }\n\n        &::-webkit-progress-value {\n          background-color: ", ";\n        }\n\n        &::-webkit-progress-bar {\n          background-color: ", ";\n        }\n      }\n    "])), label, colorMapContrast.get(color), meterColor, meterColor, backgroundColor);
  };
  /**
   * Get the styles for the stroke color
   * @param color Color
   * @returns SerializedStyles
   */

  var getStrokeColorStyles = function (color) {
    var label = "stroke_".concat(color);
    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      label: ", ";\n      stroke: ", ";\n    "])), label, colorMapAccent.get(color));
  };
  /**
   * Get the radius based on circle size
   * @param size Size [defaults to null]
   * @returns number
   */

  /**
   * Get the dash offset styles for the Circle Meter
   * @param maxDashOffset number
   * @param dashOffset number
   * @returns SerializedStyles
   */
  var getDashOffsetStyles = function (maxDashOffset, dashOffset) {
    return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      stroke-dasharray: ", ";\n      stroke-dashoffset: ", ";\n    "])), maxDashOffset.toFixed(5), dashOffset.toFixed(5));
  };
  /**
   * Get the width and height of the bounding-box / wrapper of the Icon container
   * @param size Size;
   * @returns SerializedStyles
   */

  var getIconWrapStyles = function () {
    var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    var sizeValue = isSizeSmall(size) ? sysSizeIconSm : sysSizeIconLg;
    return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      label: icon_wrap;\n      width: ", ";\n      height: ", ";\n    "])), sizeValue, sizeValue);
  };
  var base = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: progress-meter_base;\n\n    & {\n      padding: ", " ", " ", " ", ";\n      transition: value ", ";\n    }\n\n    progress {\n      vertical-align: bottom;\n      width: 100%;\n      background-clip: content-box;\n      -webkit-background-clip: content-box;\n      border: ", " solid transparent;\n      border-radius: ", ";\n      ", "\n      margin-bottom: ", ";\n\n      &::-moz-progress-bar {\n        margin-top: -", ";\n        border-radius: ", ";\n        padding: 0;\n      }\n\n      &::-webkit-progress-value {\n        margin-top: -", ";\n        border-radius: ", ";\n      }\n\n      &::-webkit-progress-bar {\n        border-radius: ", ";\n      }\n    }\n\n    ", ";\n  "])), sysSpaceXs, sysSpaceXl, sysSpaceXl, sysSpaceXl, sysMotionDurationArrive, sysBorderThicknessNormal, sysBorderRadiusSm, getProgressMeterHeightStyles(sysSizeIndicatorLg), sysSpaceSm, sysBorderThicknessNormal, sysBorderRadiusSm, sysBorderThicknessNormal, sysBorderRadiusSm, sysBorderRadiusSm, getProgressMeterColorStyles(Color.Accent3));
  var inverseLabel = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: inverse_label;\n    color: ", ";\n  "])), sysColorBackgroundSurface);
  var inverseLabelSteps = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: inverse_steps;\n    color: ", ";\n  "])), sysColorSystemContrast);
  var inverseLabelTitle = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: inverse_title;\n    color: ", ";\n  "])), sysColorBackgroundSurface);
  var label = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: label;\n    ", ";\n  "])), textBody({}));
  var steps = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: steps;\n    ", "\n    letter-spacing: normal;\n    margin-right: ", ";\n\n    [dir='rtl'] & {\n      margin-left: ", ";\n    }\n  "])), textCaptionStrong({}), sysSpaceMd, sysSpaceXs);
  var title = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: title;\n    ", "\n    letter-spacing: normal;\n  "])), textCaption({}));
  var labelSteps = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: label_steps;\n    ", "\n    margin-right: ", ";\n  "])), textBody({}), sysSpaceXs);
  var labelTitle = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: label_title;\n    ", "\n  "])), textBody({
    color: sysColorBackgroundMediumContrast
  }));
  var sizeSm = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: size_sm;\n    progress {\n      ", "\n    }\n  "])), getProgressMeterHeightStyles(sysSizeIndicatorMd));
  var sizeSmLabel = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: size_sm_label;\n    ", "\n  "])), textCaption({}));
  var sizeSmLabelSteps = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: size_sm_steps;\n    ", "\n  "])), textCaption({}));
  var sizeSmLabelTitle = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: size_sm_title;\n    ", "\n  "])), textCaption({
    color: sysColorBackgroundMediumContrast
  }));
  var sizeSmInverseLabelSteps = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: size_sm_inverse_label_steps;\n    color: ", ";\n  "])), sysColorSystemContrast);
  var sizeSmInverseLabelTitle = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: size_sm_inverse_label_title;\n    color: ", ";\n  "])), sysColorBackgroundSurface); // Circle progress meter Styles

  var circleBase = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    label: circle-progress-meter_base;\n    position: relative;\n    border-radius: ", ";\n    border: ", "rem solid ", ";\n    width: ", "rem;\n    height: ", "rem;\n    background-color: ", ";\n    padding: ", ";\n    flex-shrink: 0;\n  "])), sysBorderRadiusInteractive, circleProgressMeterBorderSize, sysColorBackgroundAlt, circleProgressMeterSize, circleProgressMeterSize, sysColorBackgroundSurface, sysSpaceXs);
  var circleInner = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    label: circle_inner;\n    border-radius: ", ";\n    width: 100%;\n    height: 100%;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    ", "\n  "])), sysBorderRadiusInteractive, getColorStyles(Color.Accent2));
  var circleInnerImage = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    label: circle_inner_image;\n    width: 100%;\n    height: 100%;\n    border-radius: inherit;\n  "])));
  var circleProgressIndicator = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    label: progress_indicator;\n    position: absolute;\n    top: -", "rem;\n    left: -", "rem;\n    width: ", "rem;\n    height: ", "rem;\n    fill: none;\n    stroke-width: ", "%;\n    stroke-linecap: round;\n    transform: rotate(-90deg);\n    transform-origin: 50% 50%;\n    ", "\n  "])), circleProgressMeterBorderSize, circleProgressMeterBorderSize, circleProgressMeterSize, circleProgressMeterSize, progressIndicatorCircleStrokeWidth.toFixed(5), getStrokeColorStyles(Color.Accent2));
  var circleSm = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    label: size_sm;\n    width: ", "rem;\n    height: ", "rem;\n    border-width: ", ";\n  "])), circleProgressMeterSmSize, circleProgressMeterSmSize, circleProgressMeterSmBorderSize);
  var circleSmProgressIndicator = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    label: size_sm_progress_indicator;\n    top: -", ";\n    left: -", ";\n    width: ", "rem;\n    height: ", "rem;\n    stroke-width: ", "%;\n  "])), circleProgressMeterSmBorderSize, circleProgressMeterSmBorderSize, circleProgressMeterSmSize, circleProgressMeterSmSize, progressIndicatorCircleSmStrokeWidth);
  var textProgressMeterBase = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: text_progress_meter_base;\n    padding: 0;\n  "])));
  var textProgressMeterInnerText = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    label: text_progress_meter_inner_text;\n    background-color: ", ";\n  "])), sysColorBackgroundSurface);
  var textContent = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    label: text_content;\n    overflow: hidden;\n    color: ", ";\n  "])), sysColorBackgroundHighContrast);
  return {
    linear: {
      base: base,
      label: label,
      steps: steps,
      title: title,
      getProgressMeterColorStyles: getProgressMeterColorStyles,
      inverseLabel: inverseLabel,
      inverseLabelSteps: inverseLabelSteps,
      inverseLabelTitle: inverseLabelTitle,
      labelSteps: labelSteps,
      labelTitle: labelTitle,
      sizeSm: sizeSm,
      sizeSmLabel: sizeSmLabel,
      sizeSmLabelSteps: sizeSmLabelSteps,
      sizeSmLabelTitle: sizeSmLabelTitle,
      sizeSmInverseLabelSteps: sizeSmInverseLabelSteps,
      sizeSmInverseLabelTitle: sizeSmInverseLabelTitle
    },
    circle: {
      base: circleBase,
      inner: circleInner,
      progressIndicator: circleProgressIndicator,
      sizeSm: circleSm,
      progressIndicatorSizeSm: circleSmProgressIndicator,
      innerImage: circleInnerImage,
      circleRadiusSvg: circleRadiusSvg,
      getStrokeColorStyles: getStrokeColorStyles,
      getCircleRadius: function getCircleRadius() {
        var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
        return isSizeSmall(size) ? circleRadiusSvg - progressIndicatorCircleSmStrokeWidth / 2 : circleRadiusSvg - progressIndicatorCircleStrokeWidth / 2;
      },
      getDashOffsetStyles: getDashOffsetStyles,
      getIconWrapStyles: getIconWrapStyles
    },
    text: {
      base: textProgressMeterBase,
      inner: textProgressMeterInnerText,
      content: textContent
    },
    util: {
      getColorStyles: getColorStyles,
      isSizeSmall: isSizeSmall
    }
  };
}
var _excluded = ["className", "id", "max", "value", "inverse", "steps", "size", "title", "color"];
var ProgressMeter = function (_ref) {
  var _styles$util, _styles$util$isSizeSm, _styles$linear, _styles$linear2, _styles$linear3, _styles$linear4, _styles$linear5, _styles$linear5$getPr, _styles$linear6, _styles$linear7, _styles$linear8, _styles$linear9, _styles$linear10, _styles$linear11, _styles$linear12, _styles$linear13, _styles$linear14, _styles$linear15, _styles$linear16;
  var className = _ref.className,
    _ref$id = _ref.id,
    id = _ref$id === void 0 ? uniqueId('progressMeter_') : _ref$id,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 0 : _ref$max,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? 0 : _ref$value,
    _ref$inverse = _ref.inverse,
    inverse = _ref$inverse === void 0 ? false : _ref$inverse,
    steps = _ref.steps,
    size = _ref.size,
    title = _ref.title,
    color = _ref.color,
    rest = _objectWithoutProperties(_ref, _excluded);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var isSizeSmall = (_styles$util = _styles.util) === null || _styles$util === void 0 ? void 0 : (_styles$util$isSizeSm = _styles$util.isSizeSmall) === null || _styles$util$isSizeSm === void 0 ? void 0 : _styles$util$isSizeSm.call(_styles$util, size);
  var labelStyles = [(_styles$linear = _styles.linear) === null || _styles$linear === void 0 ? void 0 : _styles$linear.label, inverse && ((_styles$linear2 = _styles.linear) === null || _styles$linear2 === void 0 ? void 0 : _styles$linear2.inverseLabel), isSizeSmall && ((_styles$linear3 = _styles.linear) === null || _styles$linear3 === void 0 ? void 0 : _styles$linear3.sizeSmLabel)];
  var computedColor = colorMap[color] || color;
  var progressMeterStyles = [(_styles$linear4 = _styles.linear) === null || _styles$linear4 === void 0 ? void 0 : _styles$linear4.base, color && ((_styles$linear5 = _styles.linear) === null || _styles$linear5 === void 0 ? void 0 : (_styles$linear5$getPr = _styles$linear5.getProgressMeterColorStyles) === null || _styles$linear5$getPr === void 0 ? void 0 : _styles$linear5$getPr.call(_styles$linear5, computedColor)), isSizeSmall && ((_styles$linear6 = _styles.linear) === null || _styles$linear6 === void 0 ? void 0 : _styles$linear6.sizeSm)];
  var stepsStyles = [(_styles$linear7 = _styles.linear) === null || _styles$linear7 === void 0 ? void 0 : _styles$linear7.steps, (_styles$linear8 = _styles.linear) === null || _styles$linear8 === void 0 ? void 0 : _styles$linear8.labelSteps, inverse && ((_styles$linear9 = _styles.linear) === null || _styles$linear9 === void 0 ? void 0 : _styles$linear9.inverseLabelSteps), isSizeSmall && ((_styles$linear10 = _styles.linear) === null || _styles$linear10 === void 0 ? void 0 : _styles$linear10.sizeSmLabelSteps), isSizeSmall && inverse && ((_styles$linear11 = _styles.linear) === null || _styles$linear11 === void 0 ? void 0 : _styles$linear11.sizeSmInverseLabelSteps)];
  var titleStyles = [(_styles$linear12 = _styles.linear) === null || _styles$linear12 === void 0 ? void 0 : _styles$linear12.title, (_styles$linear13 = _styles.linear) === null || _styles$linear13 === void 0 ? void 0 : _styles$linear13.labelTitle, inverse && ((_styles$linear14 = _styles.linear) === null || _styles$linear14 === void 0 ? void 0 : _styles$linear14.inverseLabelTitle), isSizeSmall && ((_styles$linear15 = _styles.linear) === null || _styles$linear15 === void 0 ? void 0 : _styles$linear15.sizeSmLabelTitle), isSizeSmall && inverse && ((_styles$linear16 = _styles.linear) === null || _styles$linear16 === void 0 ? void 0 : _styles$linear16.sizeSmInverseLabelTitle)];
  return jsx("div", {
    css: progressMeterStyles,
    className: className,
    "data-ppui-info": "progress-meter_3.6.1"
  }, jsx("progress", _extends({
    id: id,
    max: max,
    value: value
  }, rest, {
    "data-ppui": "true"
  })), jsx("label", {
    css: labelStyles,
    htmlFor: id,
    "data-ppui": "true"
  }, steps && jsx("span", {
    css: stepsStyles,
    "data-ppui": "true"
  }, steps), title && jsx("span", {
    css: titleStyles,
    "data-ppui": "true"
  }, title)));
};
var _excluded$1 = ["className", "max", "value", "color", "label", "size", "children", "innerText"],
  _excluded2 = ["iconComponent", "size"],
  _excluded3 = ["imageSrc"];
var CircleProgressMeter = forwardRef(function (props, ref) {
  var _styles$util, _styles$util$isSizeSm, _styles$circle, _styles$circle$getCir, _styles$circle2, _styles$text, _styles$circle3, _styles$circle4, _styles$circle5, _styles$circle6, _styles$circle7, _styles$circle7$getDa, _styles$circle8, _styles$util2, _styles$util2$getColo, _styles$text2, _styles$circle9, _styles$circle10;
  var className = props.className,
    _props$max = props.max,
    max = _props$max === void 0 ? 1 : _props$max,
    _props$value = props.value,
    value = _props$value === void 0 ? 0 : _props$value,
    color = props.color,
    label = props.label,
    size = props.size,
    children = props.children,
    innerText = props.innerText,
    rest = _objectWithoutProperties(props, _excluded$1);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var _utilStyles = utilStyles(tokens),
    screenReader = _utilStyles.screenReader;
  var isSizeSmall = (_styles$util = _styles.util) === null || _styles$util === void 0 ? void 0 : (_styles$util$isSizeSm = _styles$util.isSizeSmall) === null || _styles$util$isSizeSm === void 0 ? void 0 : _styles$util$isSizeSm.call(_styles$util, size);
  var radius = (_styles$circle = _styles.circle) === null || _styles$circle === void 0 ? void 0 : (_styles$circle$getCir = _styles$circle.getCircleRadius) === null || _styles$circle$getCir === void 0 ? void 0 : _styles$circle$getCir.call(_styles$circle, size);
  var maxDashOffset = 2 * Math.PI * radius;

  /* 
    // Commenting out code related to draw out animation
     const [dashOffset, setDashOffset] = useState(maxDashOffset);
     useEffect(() => {
      const newDashOffset = maxDashOffset - (value / max) * maxDashOffset;
       setDashOffset(newDashOffset);
    }, [value]);
  */
  var computedColor = colorMap[color] || color;
  var baseStyles = [(_styles$circle2 = _styles.circle) === null || _styles$circle2 === void 0 ? void 0 : _styles$circle2.base, innerText && ((_styles$text = _styles.text) === null || _styles$text === void 0 ? void 0 : _styles$text.base), isSizeSmall && ((_styles$circle3 = _styles.circle) === null || _styles$circle3 === void 0 ? void 0 : _styles$circle3.sizeSm)];
  var iconStyles = [(_styles$circle4 = _styles.circle) === null || _styles$circle4 === void 0 ? void 0 : _styles$circle4.progressIndicator, color && ((_styles$circle5 = _styles.circle) === null || _styles$circle5 === void 0 ? void 0 : _styles$circle5.getStrokeColorStyles(computedColor)), isSizeSmall && ((_styles$circle6 = _styles.circle) === null || _styles$circle6 === void 0 ? void 0 : _styles$circle6.progressIndicatorSizeSm), (_styles$circle7 = _styles.circle) === null || _styles$circle7 === void 0 ? void 0 : (_styles$circle7$getDa = _styles$circle7.getDashOffsetStyles) === null || _styles$circle7$getDa === void 0 ? void 0 : _styles$circle7$getDa.call(_styles$circle7, maxDashOffset, maxDashOffset - value / max * maxDashOffset)];
  var circleInnerStyles = [(_styles$circle8 = _styles.circle) === null || _styles$circle8 === void 0 ? void 0 : _styles$circle8.inner, color && ((_styles$util2 = _styles.util) === null || _styles$util2 === void 0 ? void 0 : (_styles$util2$getColo = _styles$util2.getColorStyles) === null || _styles$util2$getColo === void 0 ? void 0 : _styles$util2$getColo.call(_styles$util2, computedColor)), innerText && ((_styles$text2 = _styles.text) === null || _styles$text2 === void 0 ? void 0 : _styles$text2.inner)];
  var renderContent = innerText ? function renderInnerText() {
    var _styles$text3;
    var textColor = [(_styles$text3 = _styles.text) === null || _styles$text3 === void 0 ? void 0 : _styles$text3.content];
    return size === 'sm' ? jsx(CaptionText, {
      css: textColor,
      "data-ppui": "true"
    }, innerText) : jsx(HeadingText, {
      size: "sm",
      css: textColor,
      "data-ppui": "true"
    }, innerText);
  }() : children;
  return jsx("div", {
    css: baseStyles,
    className: className,
    "data-ppui-info": "progress-meter_3.6.1"
  }, jsx("svg", {
    css: iconStyles,
    "aria-hidden": "true",
    viewBox: "0 0 100 100",
    xmlns: "http://www.w3.org/2000/svg",
    "data-ppui": "true"
  }, jsx("circle", {
    cx: (_styles$circle9 = _styles.circle) === null || _styles$circle9 === void 0 ? void 0 : _styles$circle9.circleRadiusSvg,
    cy: (_styles$circle10 = _styles.circle) === null || _styles$circle10 === void 0 ? void 0 : _styles$circle10.circleRadiusSvg,
    r: radius,
    "data-ppui": "true"
  })), jsx("div", {
    css: circleInnerStyles,
    "aria-hidden": "true",
    "data-ppui": "true"
  }, renderContent), jsx("label", {
    css: screenReader,
    "data-ppui": "true"
  }, label, jsx("progress", _extends({
    max: max,
    value: value
  }, rest, {
    ref: ref,
    "data-ppui": "true"
  }))));
});
var IconProgressMeter = forwardRef(function (props, ref) {
  var _styles$util3, _styles$util3$isSizeS, _styles$circle11, _styles$circle11$getI;
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var iconComponent = props.iconComponent,
    size = props.size,
    rest = _objectWithoutProperties(props, _excluded2);
  var iconSize = ((_styles$util3 = _styles.util) === null || _styles$util3 === void 0 ? void 0 : (_styles$util3$isSizeS = _styles$util3.isSizeSmall) === null || _styles$util3$isSizeS === void 0 ? void 0 : _styles$util3$isSizeS.call(_styles$util3, size)) ? Size.SMALL : Size.LARGE;
  return jsx(CircleProgressMeter, _extends({
    size: size,
    ref: ref
  }, rest, {
    "data-ppui": "true"
  }), jsx("div", {
    css: (_styles$circle11 = _styles.circle) === null || _styles$circle11 === void 0 ? void 0 : (_styles$circle11$getI = _styles$circle11.getIconWrapStyles) === null || _styles$circle11$getI === void 0 ? void 0 : _styles$circle11$getI.call(_styles$circle11, size),
    "data-ppui": "true"
  }, jsx(iconComponent, {
    size: iconSize,
    "data-ppui": "true"
  })));
});
var ImageProgressMeter = forwardRef(function (props, ref) {
  var _styles$circle12;
  var imageSrc = props.imageSrc,
    rest = _objectWithoutProperties(props, _excluded3);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  return jsx(CircleProgressMeter, _extends({
    ref: ref
  }, rest, {
    "data-ppui": "true"
  }), jsx("img", {
    src: imageSrc,
    css: (_styles$circle12 = _styles.circle) === null || _styles$circle12 === void 0 ? void 0 : _styles$circle12.innerImage,
    "data-ppui": "true"
  }));
});
export { CircleProgressMeter, IconProgressMeter, ImageProgressMeter, ProgressMeter, styles as progressMeterStyles };